import { UIElement } from '../ui-element.js';
import { element, trustedHTML } from '../../global/template-literals.js';
import { setAttributes, removeAttributes } from '../../global/ui-helpers.js';
import styles from './ui-link.css';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UILink
 * @element ui-link
 * @classdesc Represents a class for <code>ui-link</code> element.
 * Wraps or creates a link according to brand-book.
 * Events (like onClick, onChange etc.) and all generic attributes (aria-*, form-*) are being translated
 * straight to the generated link inside ui-link component.
 * @property {string} [label=''] {@attr label} Link text.
 * @property {string} [href="#"] {@attr href} Link url.
 * @property {UILinkTarget} [target] {@attr target} Link target.
 *  {@desc same-window: Open link in same window. Default target by browser.}
 *  {@desc new-window: Open link in new tab/window}
 * @property {UILinkLayout} [layout='basic'] {@attr layout} Link layout.
 *  {@desc basic: Basic link. Link without icon, used within text paragraphs. Default layout}
 *  {@desc primary: Primary link. With left aligned arrow icon, on the left of the label}
 *  {@desc icon: Iconed link. With center aligned icon, on the left or right of the label}
 * @property {string} glyph {@attr glyph} Icon glyph code.
 * @property {UILinkColor} [color='teal'] {@attr color} UILinkColor of icon and text. Primary layout can't be colored.
 *  {@desc teal: Teal blue. Default color for all links.}
 *  {@desc bark-70: Bark brown. For example for navigation links.}
 *  {@desc alert-red: Alert red. For example for remove/delete links.}
 * @property {UILinkIconPosition} [iconPosition='left'] {@attr icon-position} Icon position.
 *  {@desc left: Icon is on the left side of the label. Default position.}
 *  {@desc right: Icon is on the right side of the label}
 * @property {boolean} [isButton=false] {@attr is-button} Whether to use button instead of link tag.
 * @property {HTMLButtonElement} button Read-only. Shortcut to child button element.
 * @property {HTMLLinkElement} link Read-only. Shortcut to child link element.
 * @example
 * <ui-link label="Document link" href="#" glyph="redesign-document" icon-position="left"></ui-link>
 */
class UILink extends UIElement {
    /**
     * @typedef {UILinkLayouts}
     * @readonly
     */
    static get UILinkLayout() {
        return {
            Basic: 'basic',
            Primary: 'primary',
            Icon: 'icon',
        };
    }

    /**
     * @typedef {UILinkIconPositions}
     * @readonly
     */
    static get UILinkIconPosition() {
        return {
            Left: 'left',
            Right: 'right',
        };
    }

    /**
     * @typedef {UILinkTargets}
     * @readonly
     */
    static get UILinkTarget() {
        return {
            SameWindow: '_self',
            NewWindow: '_blank',
        };
    }

    /**
     * @typedef {UILinkColors}
     * @readonly
     */
    static get UILinkColor() {
        return {
            Teal: 'teal',
            Bark70: 'bark-70',
            AlertRed: 'alert-red',
        };
    }

    /**
     * @type {IProps}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                label: { type: String, default: '' },
                href: {
                    type: String,
                    default: '#',
                },
                target: String,
                layout: {
                    type: String,
                    default: UILink.UILinkLayout.Basic,
                },
                glyph: String,
                color: {
                    type: String,
                    default: UILink.UILinkColor.Teal,
                },
                iconPosition: {
                    type: String,
                    default: UILink.UILinkIconPosition.Left,
                },
                isButton: {
                    type: Boolean,
                    default: false,
                },
            },
        };
    }

    /**
     * Constructs a link element to render.
     * @private
     * @returns {HTMLElement | undefined}
     */
    getLinkElement() {
        // Label
        const innerHTML = this.innerHTML.trim();
        const label = innerHTML ? trustedHTML`${innerHTML}` : this.label;
        const labelElement = label && element`<span>${label}</span>`;

        // Label with icon
        const isLeftIcon =
            this.glyph && this.iconPosition === UILink.UILinkIconPosition.Left;
        const isRightIcon =
            this.glyph && this.iconPosition === UILink.UILinkIconPosition.Right;
        const iconElement = element`<ui-icon glyph="${this.glyph}"></ui-icon>`;
        const labelWithIconElement = element`<ui-caption>
            ${isLeftIcon && iconElement}
            ${labelElement}
            ${isRightIcon && iconElement}
        </ui-caption>`;

        // Link contents based on layout
        switch (this.layout) {
            case UILink.UILinkLayout.Basic:
            case UILink.UILinkLayout.Primary:
                return this.isButton
                    ? element`<button type="button" class="ui-link__button">${labelElement}</button>`
                    : element`<a href="${this.href}" target="${this.target || null}">${labelElement}</a>`;
            case UILink.UILinkLayout.Icon:
                return this.isButton
                    ? element`<button type="button" class="ui-link__button-with-icon">${labelWithIconElement}</button>`
                    : element`<a href="${this.href}" target="${this.target || null}">${labelWithIconElement}</a>`;
            default:
                return;
        }
    }

    /**
     * Get additional classes and attributes to set for link or button such as 'disabled' or 'onclick'.
     * @private
     * @returns {Array<string[] | {string: string}>}
     */
    getClassesAndAttributes() {
        const excludedAttributes = [
            'label',
            'icon-position',
            'color',
            'target',
            'href',
            'is-button',
            'glyph',
            'layout',
            'state',
            'class',
        ];
        const genericLinkAttributes = {};
        const cssClasses = [];
        [].filter
            .call(this.attributes, (attribute) => {
                if (attribute.name === 'class') {
                    cssClasses.push(attribute.value);
                }
                return !excludedAttributes.includes(attribute.name);
            })
            .forEach((attribute) => {
                genericLinkAttributes[attribute.name] = attribute.value;
            });
        return {
            genericClasses: cssClasses,
            genericAttributes: genericLinkAttributes,
        };
    }

    /**
     * @inheritDoc
     */
    render() {
        const { genericClasses, genericAttributes } =
            this.getClassesAndAttributes();

        const linkElement = this.getLinkElement();

        if (genericClasses?.length > 0) {
            linkElement.className = genericClasses.join(' ');
        }

        this.innerHTML = '';
        this.append(linkElement);

        if (genericAttributes) {
            setAttributes(linkElement, genericAttributes);
            removeAttributes(this, Object.keys(genericAttributes));
        }
    }
}

UILink.defineElement('ui-link', styles);
export { UILink };
